import { BusinessUnitsTableDataQuery } from 'src/apps/dashboard/services/queries/business-units-table/BusinessUnitsTable.generated';
import { BusinessUnitsTableData } from 'src/apps/dashboard/services/queries/business-units-table/BusinessUnitsTable.gql';
import {
	buildBusinessUnitsTableVariables,
	getCurrentCalculationDateCandidates
} from 'src/apps/dashboard/services/queries/business-units-table/helpers/buildVariables';
import {
	BusinessUnitsTableResult,
	EMPTY_SUMMARY,
	transformBusinessUnitsTableResults
} from 'src/apps/dashboard/services/queries/business-units-table/helpers/transformConsolidatedResults';
import { BusinessUnit } from 'src/apps/dashboard/types/DashboardTypes';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { getDateTimestamp } from 'src/common/helpers/DateFormatHelper';
import { gqlClient } from 'src/common/services/api/GraphQLRequestApi';
import { Logger } from 'src/common/services/Logger';

interface IGetBusinessUnitsTableDataParams {
	days: number;
	businessUnits: BusinessUnit[];
	filters?: IFilterOptions;
}

export const getCurrentCalculationDate = (
	scoreWebProducts: BusinessUnitsTableDataQuery['score_web_products'],
	currentCalculationDateCandidates: string[]
): string | undefined => {
	if (currentCalculationDateCandidates.length === 0) {
		return undefined;
	}

	const currentCalculationDateCandidateSet = new Set(currentCalculationDateCandidates);
	const returnedCurrentCalculationDates = new Set(
		scoreWebProducts
			?.map((scoreWebProduct) => scoreWebProduct?.calculation_date)
			.filter(
				(calculationDate): calculationDate is string =>
					typeof calculationDate === 'string' &&
					currentCalculationDateCandidateSet.has(calculationDate)
			) ?? []
	);
	const datesToCompare =
		returnedCurrentCalculationDates.size > 0
			? [...returnedCurrentCalculationDates]
			: currentCalculationDateCandidates;

	return datesToCompare.reduce<string | undefined>((latestDate, dateValue) => {
		if (!latestDate || getDateTimestamp(dateValue) > getDateTimestamp(latestDate)) {
			return dateValue;
		}
		return latestDate;
	}, undefined);
};

export const getBusinessUnitsTableData = async ({
	days,
	businessUnits,
	filters
}: IGetBusinessUnitsTableDataParams): Promise<BusinessUnitsTableResult> => {
	try {
		const variables = buildBusinessUnitsTableVariables({
			days,
			filters
		});
		const currentCalculationDateCandidates = getCurrentCalculationDateCandidates();

		const result = await gqlClient.request<BusinessUnitsTableDataQuery>(
			BusinessUnitsTableData,
			variables
		);

		const transformedResult = transformBusinessUnitsTableResults(
			result,
			businessUnits,
			days,
			getCurrentCalculationDate(result.score_web_products, currentCalculationDateCandidates)
		);

		return {
			rows: transformedResult.rows,
			total: transformedResult.rows.length,
			unfilteredTotal: transformedResult.unfilteredTotal,
			scoreWebProducts: transformedResult.scoreWebProducts,
			aggregatedScoreWebProducts: transformedResult.aggregatedScoreWebProducts,
			aggregatedScoreDeltas: transformedResult.aggregatedScoreDeltas,
			summaryUniqueIssues: transformedResult.summaryUniqueIssues,
			summaryTotalIssues: transformedResult.summaryTotalIssues
		};
	} catch (error) {
		Logger.error(
			'BusinessUnitsTableService -> getBusinessUnitsTableData failed with an error:',
			error
		);
		return {
			rows: [],
			total: 0,
			unfilteredTotal: 0,
			scoreWebProducts: [],
			aggregatedScoreWebProducts: [],
			aggregatedScoreDeltas: [],
			summaryUniqueIssues: EMPTY_SUMMARY,
			summaryTotalIssues: EMPTY_SUMMARY
		};
	}
};
